import { useCallback } from 'react'
import { extractTrayJSONData } from 'lib/utils/extractTrayJSONData'
import { enqueueSnackbar } from 'notistack'
import { InventoryProviderClient } from 'lib/apollo/inventory-config'
import { useAuth0 } from 'app/Auth'
import { GET_SURGERY_TRAYS, EDIT_TRAY_ITEM } from 'lib/apollo/schema'

export const useCompleteSurgeryTrays = () => {
  const { isAuthenticated } = useAuth0()
  const inventoryClient = isAuthenticated ? InventoryProviderClient() : null

  /**
   * Updates all trays assigned to a surgery to have a "caseComplete" status
   * @param surgeryId The ID of the surgery
   * @returns Promise that resolves to a boolean indicating success
   */
  const completeSurgeryTrays = useCallback(
    async (surgeryId: string): Promise<boolean> => {
      // Check if client is available (user is authenticated)
      if (!inventoryClient) {
        console.error('Cannot complete surgery trays: User not authenticated')
        return false
      }

      if (!surgeryId) {
        console.error('Surgery ID is required to complete trays')
        return false
      }

      try {
        // Get all trays assigned to this surgery using direct client query
        const { data } = await inventoryClient.query({
          query: GET_SURGERY_TRAYS,
          variables: { surgeryId },
          fetchPolicy: 'network-only', // Ensure we get the latest data
        })

        if (!data?.getSurgeryItems || data.getSurgeryItems.length === 0) {
          console.error('No trays found for surgery:', surgeryId)
          return false
        }

        const surgeryItems = data.getSurgeryItems

        // Track success for all trays
        let allSuccess = true
        let updatedCount = 0

        // Update each tray's status to "caseComplete"
        for (const tray of surgeryItems) {
          const trayDetails = extractTrayJSONData(tray)

          if (!trayDetails) {
            console.error('Failed to extract tray details for tray:', tray.id)
            allSuccess = false
            continue
          }

          // Skip trays that are already marked as caseComplete
          if (trayDetails.status === 'caseComplete') {
            continue
          }

          // Create updated product details with caseComplete status
          const statusDetails = {
            ...(trayDetails.statusDetails || {}),
            lastUpdatedBy: 'system',
            lastUpdatedAt: new Date().toISOString(),
          }

          const updatedProductDetails = {
            ...trayDetails,
            status: 'caseComplete',
            statusDetails,
          }

          // Prepare the input for the mutation
          const { subItems, ...trayItemWithoutSubItems } = tray

          // Execute the mutation directly
          const editResult = await inventoryClient.mutate({
            mutation: EDIT_TRAY_ITEM,
            variables: {
              input: {
                ...trayItemWithoutSubItems,
                productDetails: JSON.stringify(updatedProductDetails),
              },
            },
            refetchQueries: [
              {
                query: GET_SURGERY_TRAYS,
                variables: { surgeryId },
              },
            ],
          })

          if (editResult?.data?.editTrayItem) {
            updatedCount++
          } else {
            console.error('Failed to update tray status for tray:', tray.id)
            allSuccess = false
          }
        }

        if (allSuccess && updatedCount > 0) {
          enqueueSnackbar(`${updatedCount} trays marked as case complete`, {
            variant: 'success',
          })
          return true
        } else if (updatedCount === 0) {
          enqueueSnackbar('No trays needed to be updated', { variant: 'info' })
          return true
        } else {
          enqueueSnackbar('Some trays could not be updated', {
            variant: 'warning',
          })
          return false
        }
      } catch (error) {
        console.error('Error completing surgery trays:', error)
        enqueueSnackbar('Failed to update tray statuses', { variant: 'error' })
        return false
      }
    },
    [inventoryClient]
  )

  return { completeSurgeryTrays }
}
